/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] };
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never };
import { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
import { api } from './base-api';
export enum AccessLevel {
  Free = 'FREE',
  Login = 'LOGIN',
  Plus = 'PLUS',
  Pluspro = 'PLUSPRO',
  Premium = 'PREMIUM',
  Sport = 'SPORT',
  Total = 'TOTAL'
}

export type AssetCriteria = {
  assetId: number;
  provider: string;
};

/** The type of the asset ingest */
export enum AssetIngestType {
  /** Ingest is cutting asset */
  Cutting = 'CUTTING',
  /** Ingest is transcoding asset */
  Transcoding = 'TRANSCODING'
}

/** Metadata to modify (provide only the fields you want to set or remove, to remove provide null/false value) */
export type AssetPodmeMetadataInput = {
  podmeId?: number | null | undefined;
  sendToPodme?: boolean | null | undefined;
};

export enum AssetScheduleActions {
  Publish = 'PUBLISH',
  Unpublish = 'UNPUBLISH'
}

export type AssetScheduleInput = {
  /** Unix timestamp when the asset should be playable */
  startTime: number;
};

export type AudioChannelsMix = {
  stereoMix?: StereoMix | null | undefined;
};

/** Metadata to modify (provide only the fields you want to set or remove, to remove provide null/false value) */
export type CategoryPodmeMetadataInput = {
  podmeCategories?: Array<number> | null | undefined;
  podmeId?: number | null | undefined;
  sendRssToPodme?: boolean | null | undefined;
  sendToPodme?: boolean | null | undefined;
};

/** Basic stream metadata to be saved during creation. */
export type CreateLiveStreamMetadataInput = {
  /** Asset access. Should be used in conjunction with LiveStreamProtection of HLSStreamInput */
  access?: string | null | undefined;
  /** Category in which asset will be created. */
  categoryId: number;
  /** User name or email. Should be set when setting metadata first time. */
  createdBy?: string | null | undefined;
  /** Description of the asset. */
  description?: string | null | undefined;
  /** Title of the asset. */
  title: string;
};

export type CutRange = {
  begin: number;
  end: number;
};

export enum DrmJobStatus {
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Packaging = 'PACKAGING',
  Queued = 'QUEUED',
  Transcoding = 'TRANSCODING'
}

export enum ElementalInputType {
  Sdi = 'SDI',
  St2110 = 'ST2110'
}

export type EncoderScheduleInput = {
  duration?: number | null | undefined;
  /** Unix timestamp when the encoder should be automatically started */
  startTime?: number | null | undefined;
};

export enum EncodingProfile {
  Fast = 'FAST',
  Hq = 'HQ',
  Normal = 'NORMAL'
}

export type ExternalSourceInput = {
  url: string;
};

export enum GeoRestriction {
  Norway = 'NORWAY',
  Sweden = 'SWEDEN'
}

export type HlsStreamInput = {
  audioChannelsMix?: AudioChannelsMix | null | undefined;
  dvrLength: number;
  profile?: HlsProfile;
  protections?: Array<LiveStreamProtection>;
};

export enum HlsProfile {
  Hls = 'HLS',
  Hls1080 = 'HLS1080'
}

export type LiveArchiveCutRangeInput = {
  /** Live archive start time in seconds */
  begin?: number | null | undefined;
  /** Live archive end time in seconds */
  end?: number | null | undefined;
};

export enum LiveEventNotificationLevel {
  Alert = 'ALERT',
  Error = 'ERROR',
  Info = 'INFO'
}

export enum LiveEventStatus {
  Archived = 'ARCHIVED',
  Cancelled = 'CANCELLED',
  Complete = 'COMPLETE',
  Error = 'ERROR',
  Pending = 'PENDING',
  Running = 'RUNNING'
}

export enum LiveStreamBackup {
  Medialive = 'MEDIALIVE',
  Network = 'NETWORK',
  Sdi = 'SDI'
}

export type LiveStreamBackupInput = {
  medialive?: MediaLiveSourceInput | null | undefined;
  network?: NetworkSourceInput | null | undefined;
  sdi?: SdiSourceInput | null | undefined;
  type: LiveStreamBackup;
};

export type LiveStreamDestinationInput = {
  server: string;
  streamKey: string;
};

export type LiveStreamHtmlOverlayInput = {
  url: string;
};

export type LiveStreamOverlayInput = {
  /**
   * URL of the image to be inserted as an overlay.
   *
   * Requirements for the overlay:
   * 1. Transparent background. Saved in a format supporting transparency. PNG is recommended.
   * 2. The size has to be the same as the input video size. Overlay image is going to be inserted starting from top-left corner, so sizes has to be the same to avoid cropping.
   */
  url: string;
};

export enum LiveStreamProtection {
  Encrypted = 'ENCRYPTED',
  Geoblocked = 'GEOBLOCKED',
  TokenSecured = 'TOKEN_SECURED'
}

export enum LiveStreamProtocol {
  Hls = 'HLS'
}

export type LiveStreamProtocolInput = {
  hls?: HlsStreamInput | null | undefined;
  type: LiveStreamProtocol;
};

/** Controls automatic live schedule */
export type LiveStreamScheduleInput = {
  asset?: AssetScheduleInput | null | undefined;
  encoder?: EncoderScheduleInput | null | undefined;
};

export type LiveStreamSetupInput = {
  /** Backup encoder */
  backup?: LiveStreamBackupInput | null | undefined;
  /** Stream destination */
  destination?: LiveStreamDestinationInput | null | undefined;
  /** Protocol configuration */
  protocol?: LiveStreamProtocolInput | null | undefined;
  /** Primary encoder */
  source?: LiveStreamSourceInput | null | undefined;
};

export enum LiveStreamSource {
  External = 'EXTERNAL',
  Medialive = 'MEDIALIVE',
  Network = 'NETWORK',
  Sdi = 'SDI'
}

export type LiveStreamSourceInput = {
  external?: ExternalSourceInput | null | undefined;
  medialive?: MediaLiveSourceInput | null | undefined;
  network?: NetworkSourceInput | null | undefined;
  sdi?: SdiSourceInput | null | undefined;
  type: LiveStreamSource;
};

export enum MediaLiveChannelState {
  Creating = 'CREATING',
  Idle = 'IDLE',
  Running = 'RUNNING',
  Starting = 'STARTING',
  Stopping = 'STOPPING'
}

export enum MediaLiveInputType {
  Link = 'LINK',
  Pull = 'PULL',
  Push = 'PUSH'
}

export type MediaLiveSourceInput = {
  reserved?: MediaLiveSourceInputReserved | null | undefined;
  srt?: MediaLiveSourceInputSrt | null | undefined;
  type: MediaLiveSourceInputType;
  url?: string | null | undefined;
};

export type MediaLiveSourceInputReserved = {
  /** Existing MediaLive Input ID */
  inputId: string;
};

export type MediaLiveSourceInputSrt = {
  algorithm?: string | null | undefined;
  latency?: number | null | undefined;
  passphrase?: string | null | undefined;
  streamId?: string | null | undefined;
};

export enum MediaLiveSourceInputType {
  Pull = 'PULL',
  Push = 'PUSH',
  Reserved = 'RESERVED'
}

export type NetworkSourceInput = {
  encoderId: string;
  url: string;
};

export enum NotificationScheduleActions {
  Push = 'PUSH'
}

export type Overlay = {
  params?: OverlayParams | null | undefined;
  sourceUri: string;
};

export enum OverlayOrigin {
  BottomLeft = 'BOTTOM_LEFT',
  BottomRight = 'BOTTOM_RIGHT',
  TopLeft = 'TOP_LEFT',
  TopRight = 'TOP_RIGHT'
}

export type OverlayParams = {
  height: number;
  origin: OverlayOrigin;
  width: number;
  x: number;
  y: number;
};

export type PlaylistAssetInput = {
  id: number;
  provider: string;
};

export type PlaylistItemInput = {
  asset?: PlaylistAssetInput | null | undefined;
  playlist?: PlaylistItemPlaylistInput | null | undefined;
  position: number;
};

export type PlaylistItemPlaylistInput = {
  id: string;
  provider: string;
};

export type PlaylistLabelInput = {
  id: string;
  name: string;
  type?: string;
};

/** Basic stream metadata to be saved during update. */
export type PrepareLiveStreamMetadataInput = {
  /** Asset access. Should be used in conjunction with LiveStreamProtection of HLSStreamInput */
  access?: string | null | undefined;
  /** User name or email. */
  editedBy?: string | null | undefined;
};

export type Protections = {
  /** Access level identifier to apply to the output stream */
  accessLevel?: AccessLevel | null | undefined;
  /** Geographical area to limit the output stream availability */
  geoRestriction?: GeoRestriction | null | undefined;
};

export type RoleInput = {
  newsroom: string;
  roleType: string;
};

export enum RtmpStreamTypeInput {
  Audio = 'AUDIO',
  Video = 'VIDEO'
}

export type SdiSourceInput = {
  encoderId: string;
  inputId: number;
};

export type SourceAudioChannel = {
  channelId: number;
  gain: number;
};

export type StereoMix = {
  leftChannel: Array<SourceAudioChannel>;
  rightChannel: Array<SourceAudioChannel>;
};

export enum StreamUrlFormat {
  Hds = 'HDS',
  Hls = 'HLS',
  Hlsdrm = 'HLSDRM',
  Mp4 = 'MP4'
}

export type TranscodeVideoOptions = {
  encodingProfile?: EncodingProfile | null | undefined;
  protections?: Protections | null | undefined;
  transformations?: Transformations | null | undefined;
};

/** Video transformations to be performed */
export type Transformations = {
  /** Media file cut specification */
  cuts?: Array<WeakCutRange> | null | undefined;
  /** Image overlay specification */
  overlays?: Array<Overlay> | null | undefined;
  preservePoster?: boolean | null | undefined;
  preservePreview?: boolean | null | undefined;
};

export type UpdateLiveStreamMetadataInput = {
  /** User name or email. */
  editedBy: string;
};

export type WeakCutRange = {
  begin: number;
  end?: number | null | undefined;
};

export type AssetHistoryQueryVariables = Exact<{
  newsroom: string;
  assetId: number;
}>;


export type AssetHistoryQuery = { assetHistory: Array<unknown> };

export type CategoryHistoryQueryVariables = Exact<{
  newsroom: string;
  categoryId: number;
}>;


export type CategoryHistoryQuery = { categoryHistory: Array<unknown> };

export type AuthorDataFieldsFragment = { title: string | null, slug: string | null, id: string | null, contact: Array<{ type: string | null, value: string | null }> | null };

export type TagDataFieldsFragment = { id: string, title: string, type: string };

export type StoryDataFieldsFragment = { id: string, title: string };

export type SearchAuthorsQueryVariables = Exact<{
  provider: string;
  query: string;
}>;


export type SearchAuthorsQuery = { searchAuthors: Array<{ title: string | null, slug: string | null, id: string | null, contact: Array<{ type: string | null, value: string | null }> | null }> };

export type AuthorsQueryVariables = Exact<{
  provider: string;
  query: Array<string> | string;
}>;


export type AuthorsQuery = { authors: Array<{ title: string | null, slug: string | null, id: string | null, contact: Array<{ type: string | null, value: string | null }> | null }> };

export type SearchTagsQueryVariables = Exact<{
  provider: string;
  query: string;
  page?: number | null | undefined;
  limit?: number | null | undefined;
}>;


export type SearchTagsQuery = { searchTags: Array<{ id: string, title: string, type: string }> };

export type SuggestTagsQueryVariables = Exact<{
  provider: string;
  title: string;
  description?: string | null | undefined;
  tags?: Array<string> | string | null | undefined;
  subtitlesUrls?: Array<string> | string | null | undefined;
  limit?: number | null | undefined;
}>;


export type SuggestTagsQuery = { suggestTags: Array<{ id: string, title: string, type: string }> };

export type ListTagsQueryVariables = Exact<{
  provider: string;
  ids: Array<string> | string;
}>;


export type ListTagsQuery = { listTags: Array<{ id: string, title: string, type: string }> };

export type StoriesQueryVariables = Exact<{
  provider: string;
  ids: Array<string> | string;
}>;


export type StoriesQuery = { stories: Array<{ id: string, title: string }> };

export type SearchStoriesQueryVariables = Exact<{
  provider: string;
  query: string;
  page?: number | null | undefined;
  limit?: number | null | undefined;
}>;


export type SearchStoriesQuery = { searchStories: Array<{ id: string, title: string }> };

export type CreateAssetPreviewMutationVariables = Exact<{
  assetId: number;
  provider: string;
  editedBy: string;
  cutRange: CutRange;
  artifacts: Array<string> | string;
}>;


export type CreateAssetPreviewMutation = { createAssetPreview:
    | { result: boolean, message: string }
    | { result: boolean }
   };

export type UploadAssetPreviewMutationVariables = Exact<{
  assetId: number;
  provider: string;
  editedBy: string;
  source: string;
  prefix?: string | null | undefined;
  artifacts: Array<string> | string;
}>;


export type UploadAssetPreviewMutation = { uploadAssetPreview:
    | { result: boolean, message: string }
    | { result: boolean }
   };

export type UploadCategoryPreviewMutationVariables = Exact<{
  categoryId: number;
  provider: string;
  editedBy: string;
  source: string;
  artifacts: Array<string> | string;
}>;


export type UploadCategoryPreviewMutation = { uploadCategoryPreview:
    | { message: string, result: boolean }
    | { result: boolean }
   };

type LastAssetIngestData_AssetIngestDone_Fragment = { __typename: 'AssetIngestDone', assetId: number | null, provider: string | null, type: AssetIngestType | null };

type LastAssetIngestData_AssetIngestFailed_Fragment = { __typename: 'AssetIngestFailed', assetId: number | null, provider: string | null, type: AssetIngestType | null };

type LastAssetIngestData_AssetIngestInProgress_Fragment = { __typename: 'AssetIngestInProgress', assetId: number | null, provider: string | null, type: AssetIngestType | null, totalProgress: number | null };

type LastAssetIngestData_AssetIngestNone_Fragment = { __typename: 'AssetIngestNone', assetId: number | null, provider: string | null };

export type LastAssetIngestDataFragment =
  | LastAssetIngestData_AssetIngestDone_Fragment
  | LastAssetIngestData_AssetIngestFailed_Fragment
  | LastAssetIngestData_AssetIngestInProgress_Fragment
  | LastAssetIngestData_AssetIngestNone_Fragment
;

export type LastAssetIngestQueryVariables = Exact<{
  assetCriteria: AssetCriteria;
}>;


export type LastAssetIngestQuery = { lastAssetIngest:
    | { __typename: 'AssetIngestDone', assetId: number | null, provider: string | null, type: AssetIngestType | null }
    | { __typename: 'AssetIngestFailed', assetId: number | null, provider: string | null, type: AssetIngestType | null }
    | { __typename: 'AssetIngestInProgress', assetId: number | null, provider: string | null, type: AssetIngestType | null, totalProgress: number | null }
    | { __typename: 'AssetIngestNone', assetId: number | null, provider: string | null }
   | null };

export type LastAssetsIngestQueryVariables = Exact<{
  assetsCriteria: Array<AssetCriteria> | AssetCriteria;
}>;


export type LastAssetsIngestQuery = { lastAssetsIngest: Array<
    | { __typename: 'AssetIngestDone', assetId: number | null, provider: string | null, type: AssetIngestType | null }
    | { __typename: 'AssetIngestFailed', assetId: number | null, provider: string | null, type: AssetIngestType | null }
    | { __typename: 'AssetIngestInProgress', assetId: number | null, provider: string | null, type: AssetIngestType | null, totalProgress: number | null }
    | { __typename: 'AssetIngestNone', assetId: number | null, provider: string | null }
   | null> | null };

export type LiveEncodersQueryVariables = Exact<{
  newsroom: string;
}>;


export type LiveEncodersQuery = { encoders: Array<{ id: string, name: string, url: string, additionalFeatures: Array<string> | null, inputs: Array<{ id: number, name: string | null, type: ElementalInputType | null, event: { id: number, status: LiveEventStatus, schedule: { startTime: number | null, endTime: number | null } | null, asset: { id: number, provider: string } | null } | null }>, events: Array<{ id: number, createTime: number, status: LiveEventStatus, networkInputUri: string | null, hasLivePreview: boolean | null, schedule: { startTime: number | null, endTime: number | null } | null, encoder: { id: string, name: string } | null, input: { id: number, name: string | null } | null, asset: { id: number, provider: string, additional: { metadata: unknown } } | null, notifications: Array<{ timestamp: string, level: LiveEventNotificationLevel, message: string, code: number }> }> }>, channels: Array<{ id: number, state: MediaLiveChannelState, input: { type: MediaLiveInputType, urls: Array<string>, srtSettings: Array<{ streamId: string | null, address: string | null, port: string | null, latency: number | null, algorithm: string | null, passphrase: string | null }> }, asset: { id: number, provider: string }, schedule: { startTime: number | null, endTime: number | null } | null }> };

export type MediaLiveInputsQueryVariables = Exact<{
  newsroom: string;
  reserved?: boolean | null | undefined;
}>;


export type MediaLiveInputsQuery = { mediaLiveInputs: Array<{ id: string, name: string, type: MediaLiveInputType, urls: Array<string>, channel: { asset: { id: number, provider: string } } | null, srtSettings: Array<{ streamId: string | null, address: string | null, port: string | null, latency: number | null, algorithm: string | null, passphrase: string | null }> }> };

export type CuttingroomChannelsQueryVariables = Exact<{
  newsroom: string;
}>;


export type CuttingroomChannelsQuery = { cuttingroomChannels: Array<{ name: string, server: string, streamKey: string }> };

export type LiveArchiveQueryVariables = Exact<{
  newsroom: string;
  assetId: number;
}>;


export type LiveArchiveQuery = { liveArchive: { logoUrl: string | null } | null };

export type CreateLiveStreamMutationVariables = Exact<{
  newsroom: string;
  metadata: CreateLiveStreamMetadataInput;
  schedule?: LiveStreamScheduleInput | null | undefined;
  livePreview?: boolean | null | undefined;
  liveArchive?: boolean | null | undefined;
  vertical?: boolean | null | undefined;
  setup: LiveStreamSetupInput;
  overlay?: LiveStreamOverlayInput | null | undefined;
  htmlOverlay?: LiveStreamHtmlOverlayInput | null | undefined;
  deinterlace?: boolean | null | undefined;
  audioNormalization?: boolean | null | undefined;
  audioOnly?: boolean | null | undefined;
  wscRtmpOutput?: boolean | null | undefined;
  drmEncryption?: boolean | null | undefined;
}>;


export type CreateLiveStreamMutation = { createLiveStream: { asset: { id: number, provider: string } } };

export type PrepareLiveStreamMutationVariables = Exact<{
  newsroom: string;
  assetId: number;
  metadata?: PrepareLiveStreamMetadataInput | null | undefined;
  schedule?: LiveStreamScheduleInput | null | undefined;
  livePreview?: boolean | null | undefined;
  liveArchive?: boolean | null | undefined;
  vertical?: boolean | null | undefined;
  setup: LiveStreamSetupInput;
  overlay?: LiveStreamOverlayInput | null | undefined;
  htmlOverlay?: LiveStreamHtmlOverlayInput | null | undefined;
  deinterlace?: boolean | null | undefined;
  audioNormalization?: boolean | null | undefined;
  audioOnly?: boolean | null | undefined;
  wscRtmpOutput?: boolean | null | undefined;
  drmEncryption?: boolean | null | undefined;
}>;


export type PrepareLiveStreamMutation = { prepareLiveStream: { asset: { id: number, provider: string } } };

export type StartLiveStreamMutationVariables = Exact<{
  newsroom: string;
  assetId: number;
  editedBy?: string | null | undefined;
}>;


export type StartLiveStreamMutation = { startLiveStream: boolean };

export type StopLiveStreamMutationVariables = Exact<{
  newsroom: string;
  assetId: number;
  editedBy?: string | null | undefined;
}>;


export type StopLiveStreamMutation = { stopLiveStream: boolean };

export type UpdateLiveStreamMutationVariables = Exact<{
  newsroom: string;
  assetId: number;
  schedule?: LiveStreamScheduleInput | null | undefined;
  metadata: UpdateLiveStreamMetadataInput;
}>;


export type UpdateLiveStreamMutation = { updateLiveStream: boolean };

export type InsertAdMutationVariables = Exact<{
  newsroom: string;
  assetId: number;
  duration: number;
  offset?: number | null | undefined;
}>;


export type InsertAdMutation = { insertAd: boolean };

export type CancelAdMutationVariables = Exact<{
  newsroom: string;
  assetId: number;
}>;


export type CancelAdMutation = { cancelAd: boolean };

export type UnpublishLiveAssetMutationVariables = Exact<{
  newsroom: string;
  assetId: number;
  editedBy?: string | null | undefined;
}>;


export type UnpublishLiveAssetMutation = { unpublishLiveAsset: boolean };

export type EncoderInputPreviewQueryVariables = Exact<{
  encoderId: string;
  inputId: number;
  width: number;
  height: number;
}>;


export type EncoderInputPreviewQuery = { encoderInputPreview: { base64: string } };

export type LiveStreamQueryVariables = Exact<{
  newsroom: string;
  assetId: number;
}>;


export type LiveStreamQuery = { live:
    | { __typename: 'ElementalLive', events: Array<
        | { __typename: 'ElementalNetworkEvent', dvrLength: number | null, hasLivePreview: boolean, hasWscRtmpOutput: boolean | null, audioChannelsMix: { stereoMix: { leftChannel: Array<{ channelId: number, gain: number }>, rightChannel: Array<{ channelId: number, gain: number }> } | null } | null }
        | { __typename: 'ElementalSdiEvent', dvrLength: number | null, hasLivePreview: boolean, hasWscRtmpOutput: boolean | null, audioChannelsMix: { stereoMix: { leftChannel: Array<{ channelId: number, gain: number }>, rightChannel: Array<{ channelId: number, gain: number }> } | null } | null }
      > }
    | { __typename: 'ExternalLive', externalUrl: string }
    | { __typename: 'MediaLiveLive' }
    | { __typename: 'RtmpLive', streamKey: string }
   };

export type CreateRtmpPushMutationVariables = Exact<{
  metadata: CreateLiveStreamMetadataInput;
  newsroom: string;
  streamKey: string;
  streamType: RtmpStreamTypeInput;
}>;


export type CreateRtmpPushMutation = { createRtmpPush: { asset: { id: number, provider: string } } };

export type PrepareRtmpPushMutationVariables = Exact<{
  metadata: PrepareLiveStreamMetadataInput;
  newsroom: string;
  assetId: number;
  streamKey: string;
  streamType: RtmpStreamTypeInput;
}>;


export type PrepareRtmpPushMutation = { prepareRtmpPush: { asset: { id: number, provider: string } } };

export type DuplicateLiveStreamMutationVariables = Exact<{
  newsroom: string;
  assetId: number;
  duplicatedBy: string;
}>;


export type DuplicateLiveStreamMutation = { duplicateLiveStream: { asset: { id: number, provider: string } } };

export type ChangeAssetRestrictionsMutationVariables = Exact<{
  provider: string;
  assetId: number;
  geoRestriction?: GeoRestriction | null | undefined;
  accessLevel?: AccessLevel | null | undefined;
}>;


export type ChangeAssetRestrictionsMutation = { changeAssetRestrictions: boolean };

export type UploadSubtitlesMutationVariables = Exact<{
  provider: string;
  assetId: number;
  languageCode: string;
  source: string;
}>;


export type UploadSubtitlesMutation = { uploadSubtitles: { url: string } };

export type StreamDurationQueryVariables = Exact<{
  provider: string;
  assetId: number;
}>;


export type StreamDurationQuery = { streamDuration: number | null };

export type SignAssetStreamUrlsMutationVariables = Exact<{
  provider: string;
  assetId: number;
  expirationTime: number;
}>;


export type SignAssetStreamUrlsMutation = { signAssetStreamUrls: Array<{ format: StreamUrlFormat, url: string }> };

export type GenerateDrmTokenMutationVariables = Exact<{
  provider: string;
  assetId: number;
}>;


export type GenerateDrmTokenMutation = { generateDrmToken: { token: string, expiry: number } };

export type PlaylistFragment = { id: string, name: string, provider: string, header: string | null, searchQuery: string | null, locked: boolean, metadata: unknown, partnerName: string | null, partnerPlaylistAlias: string | null, labels: Array<{ id: string, name: string, type: string }>, items: Array<
    | { position: number, asset: { id: number, provider: string } }
    | { position: number, playlist: { id: string, provider: string } }
  > };

export type PlaylistsQueryVariables = Exact<{
  provider: string;
  id?: string | null | undefined;
  name?: string | null | undefined;
  labels?: Array<string> | string;
  limit?: number | null | undefined;
  page?: number | null | undefined;
}>;


export type PlaylistsQuery = { providerPlaylists: Array<{ id: string, name: string, provider: string, header: string | null, searchQuery: string | null, locked: boolean, metadata: unknown, partnerName: string | null, partnerPlaylistAlias: string | null, labels: Array<{ id: string, name: string, type: string }>, items: Array<
      | { position: number, asset: { id: number, provider: string } }
      | { position: number, playlist: { id: string, provider: string } }
    > }> };

export type CreatePlaylistMutationVariables = Exact<{
  provider: string;
  name: string;
  header?: string | null | undefined;
  labels?: Array<PlaylistLabelInput> | PlaylistLabelInput | null | undefined;
  searchQuery?: string | null | undefined;
  metadata?: unknown;
  items: Array<PlaylistItemInput> | PlaylistItemInput;
  partnerName?: string | null | undefined;
  partnerPlaylistAlias?: string | null | undefined;
}>;


export type CreatePlaylistMutation = { createPlaylist: { success: boolean, errors: Array<string> | null, playlist: { id: string, name: string, provider: string, header: string | null, searchQuery: string | null, locked: boolean, metadata: unknown, partnerName: string | null, partnerPlaylistAlias: string | null, labels: Array<{ id: string, name: string, type: string }>, items: Array<
        | { position: number, asset: { id: number, provider: string } }
        | { position: number, playlist: { id: string, provider: string } }
      > } | null } };

export type UpdatePlaylistMutationVariables = Exact<{
  id: string;
  provider: string;
  name: string;
  header?: string | null | undefined;
  labels?: Array<PlaylistLabelInput> | PlaylistLabelInput | null | undefined;
  searchQuery?: string | null | undefined;
  metadata?: unknown;
  items: Array<PlaylistItemInput> | PlaylistItemInput;
  partnerName?: string | null | undefined;
  partnerPlaylistAlias?: string | null | undefined;
}>;


export type UpdatePlaylistMutation = { updatePlaylist: { success: boolean, errors: Array<string> | null, playlist: { id: string, name: string, provider: string, header: string | null, searchQuery: string | null, locked: boolean, metadata: unknown, partnerName: string | null, partnerPlaylistAlias: string | null, labels: Array<{ id: string, name: string, type: string }>, items: Array<
        | { position: number, asset: { id: number, provider: string } }
        | { position: number, playlist: { id: string, provider: string } }
      > } | null } };

export type DeletePlaylistMutationVariables = Exact<{
  id: string;
  provider: string;
}>;


export type DeletePlaylistMutation = { deletePlaylist: { success: boolean, playlist: { id: string, items: Array<
        | { position: number, asset: { id: number, provider: string } }
        | { position: number, playlist: { id: string, provider: string } }
      > } | null } };

export type PodmeCategoriesQueryVariables = Exact<{ [key: string]: never; }>;


export type PodmeCategoriesQuery = { podmeCategories: Array<{ id: number, name: string | null }> };

export type AssetPodmeMetadataQueryVariables = Exact<{
  provider: string;
  assetId: number;
}>;


export type AssetPodmeMetadataQuery = { assetPodmeMetadata: { sendToPodme: boolean, podmeId: number | null } };

export type CategoryPodmeMetadataQueryVariables = Exact<{
  provider: string;
  categoryId: number;
}>;


export type CategoryPodmeMetadataQuery = { categoryPodmeMetadata: { sendToPodme: boolean, podmeId: number | null, podmeCategories: Array<number>, sendRssToPodme: boolean } };

export type ModifyAssetPodmeMetadataMutationVariables = Exact<{
  provider: string;
  assetId: number;
  metadata: AssetPodmeMetadataInput;
}>;


export type ModifyAssetPodmeMetadataMutation = { modifyAssetPodmeMetadata: { sendToPodme: boolean, podmeId: number | null } };

export type ModifyCategoryPodmeMetadataMutationVariables = Exact<{
  provider: string;
  categoryId: number;
  metadata: CategoryPodmeMetadataInput;
}>;


export type ModifyCategoryPodmeMetadataMutation = { modifyCategoryPodmeMetadata: { sendToPodme: boolean, podmeId: number | null, podmeCategories: Array<number>, sendRssToPodme: boolean } };

export type SendPushNotificationMutationVariables = Exact<{
  provider: string;
  assetId: number;
  notificationBody?: string | null | undefined;
}>;


export type SendPushNotificationMutation = { sendPushNotification:
    | { __typename: 'SendPushNotificationErrorResponse', errorMessage: string }
    | { __typename: 'SendPushNotificationSuccessResponse', pushIds: Array<string>, numRecipients: number }
   | null };

export type AssetSchedulesQueryVariables = Exact<{
  provider?: string | null | undefined;
  assetId?: number | null | undefined;
  action: AssetScheduleActions;
}>;


export type AssetSchedulesQuery = { assetSchedules: Array<{ provider: string, assetId: number, time: number, action: AssetScheduleActions }> | null };

export type CreateAssetScheduleMutationVariables = Exact<{
  provider: string;
  assetId: number;
  time: number;
  action: AssetScheduleActions;
}>;


export type CreateAssetScheduleMutation = { createAssetSchedule: { provider: string, assetId: number, time: number, action: AssetScheduleActions } | null };

export type UpdateAssetScheduleMutationVariables = Exact<{
  provider: string;
  assetId: number;
  time: number;
  action: AssetScheduleActions;
}>;


export type UpdateAssetScheduleMutation = { updateAssetSchedule: { provider: string, assetId: number, time: number, action: AssetScheduleActions } | null };

export type DeleteAssetScheduleMutationVariables = Exact<{
  provider: string;
  assetId: number;
  action: AssetScheduleActions;
}>;


export type DeleteAssetScheduleMutation = { deleteAssetSchedule: boolean | null };

export type NotificationSchedulesQueryVariables = Exact<{
  provider?: string | null | undefined;
  assetId?: number | null | undefined;
  action: NotificationScheduleActions;
}>;


export type NotificationSchedulesQuery = { notificationSchedules: Array<{ provider: string, assetId: number, action: NotificationScheduleActions, time: number, notificationBody: string | null }> | null };

export type CreateNotificationScheduleMutationVariables = Exact<{
  provider: string;
  assetId: number;
  action: NotificationScheduleActions;
  time: number;
  notificationBody?: string | null | undefined;
}>;


export type CreateNotificationScheduleMutation = { createNotificationSchedule: { provider: string, assetId: number, action: NotificationScheduleActions, time: number, notificationBody: string | null } | null };

export type UpdateNotificationScheduleMutationVariables = Exact<{
  provider: string;
  assetId: number;
  action: NotificationScheduleActions;
  time: number;
  notificationBody?: string | null | undefined;
}>;


export type UpdateNotificationScheduleMutation = { updateNotificationSchedule: { provider: string, assetId: number, action: NotificationScheduleActions, time: number, notificationBody: string | null } | null };

export type DeleteNotificationScheduleMutationVariables = Exact<{
  provider: string;
  assetId: number;
  action: NotificationScheduleActions;
}>;


export type DeleteNotificationScheduleMutation = { deleteNotificationSchedule: boolean | null };

export type EngagementStatsApiKeyQueryVariables = Exact<{
  provider: string;
}>;


export type EngagementStatsApiKeyQuery = { engagementStatsApiKey: { value: string | null } };

export type GenerateSubtitlesForAssetMutationVariables = Exact<{
  id: number;
  provider: string;
}>;


export type GenerateSubtitlesForAssetMutation = { generateSubtitlesForAsset: { success: boolean | null, error: string | null } | null };

export type DurationFromUrlQueryVariables = Exact<{
  url: string;
}>;


export type DurationFromUrlQuery = { playlists: Array<{ duration: number }> };

export type ConvertVideoMutationVariables = Exact<{
  provider: string;
  assetId: number;
}>;


export type ConvertVideoMutation = { convertVideo: { url: string } };

export type PrepareLiveArchiveMutationVariables = Exact<{
  provider: string;
  assetId: number;
  cutRange?: LiveArchiveCutRangeInput | null | undefined;
}>;


export type PrepareLiveArchiveMutation = { prepareLiveArchive: { url: string | null } };

export type CropLiveArchiveMutationVariables = Exact<{
  provider: string;
  assetId: number;
  cutRange: LiveArchiveCutRangeInput;
  createdBy?: string | null | undefined;
}>;


export type CropLiveArchiveMutation = { cropLiveArchive: { assetId: number | null, originAssetId: number | null } };

export type LiveArchiveExpirationQueryVariables = Exact<{
  provider: string;
  assetId: number;
}>;


export type LiveArchiveExpirationQuery = { liveArchiveExpiration: number | null };

export type CreateUserMutationVariables = Exact<{
  firstname?: string | null | undefined;
  lastname?: string | null | undefined;
  email: string;
  roles: Array<RoleInput> | RoleInput;
  created: unknown;
}>;


export type CreateUserMutation = { createUser:
    | { message: string }
    | { email: string, firstname: string | null, lastname: string | null, roles: Array<{ newsroom: string, roleType: string }> }
   | null };

export type UpdateUserMutationVariables = Exact<{
  firstname?: string | null | undefined;
  lastname?: string | null | undefined;
  email: string;
  roles: Array<RoleInput> | RoleInput;
}>;


export type UpdateUserMutation = { updateUser:
    | { message: string }
    | { email: string, firstname: string | null, lastname: string | null, roles: Array<{ newsroom: string, roleType: string }> }
   | null };

export type UsersQueryVariables = Exact<{
  query: string;
}>;


export type UsersQuery = { users: Array<{ email: string, firstname: string | null, lastname: string | null, roles: Array<{ newsroom: string, roleType: string }> }> };

export type CreateClipsMutationVariables = Exact<{
  assetId: number;
  provider: string;
  cutRanges: Array<WeakCutRange> | WeakCutRange;
  protection?: Protections | null | undefined;
}>;


export type CreateClipsMutation = { createClips:
    | { assetId: number, originAssetId: number }
    | { message: string }
   };

export type TrimAssetMutationVariables = Exact<{
  assetId: number;
  provider: string;
  cutRanges: Array<WeakCutRange> | WeakCutRange;
}>;


export type TrimAssetMutation = { trimAsset: boolean };

export type TranscodeVideoMutationVariables = Exact<{
  assetId: number;
  newsroom: string;
  sourceUri: string;
  options?: TranscodeVideoOptions | null | undefined;
}>;


export type TranscodeVideoMutation = { transcodeVideo: { assetId: number, requestId: string } };

export type EnableDrmMutationVariables = Exact<{
  assetId: number;
  provider: string;
}>;


export type EnableDrmMutation = { enableDrm: { status: DrmJobStatus } };

export class TypedDocumentString<TResult, TVariables>
  extends String
  implements DocumentTypeDecoration<TResult, TVariables>
{
  __apiType?: NonNullable<DocumentTypeDecoration<TResult, TVariables>['__apiType']>;
  private value: string;
  public __meta__?: Record<string, any> | undefined;

  constructor(value: string, __meta__?: Record<string, any> | undefined) {
    super(value);
    this.value = value;
    this.__meta__ = __meta__;
  }

  override toString(): string & DocumentTypeDecoration<TResult, TVariables> {
    return this.value;
  }
}
export const AuthorDataFieldsFragmentDoc = new TypedDocumentString(`
    fragment AuthorDataFields on Author {
  title
  slug
  id
  contact {
    type
    value
  }
}
    `, {"fragmentName":"AuthorDataFields"});
export const TagDataFieldsFragmentDoc = new TypedDocumentString(`
    fragment TagDataFields on Tag {
  id
  title
  type
}
    `, {"fragmentName":"TagDataFields"});
export const StoryDataFieldsFragmentDoc = new TypedDocumentString(`
    fragment StoryDataFields on Story {
  id
  title
}
    `, {"fragmentName":"StoryDataFields"});
export const LastAssetIngestDataFragmentDoc = new TypedDocumentString(`
    fragment LastAssetIngestData on LastAssetIngest {
  __typename
  ... on AssetIngestNone {
    assetId
    provider
  }
  ... on AssetIngest {
    assetId
    provider
    type
  }
  ... on AssetIngestInProgress {
    totalProgress
  }
}
    `, {"fragmentName":"LastAssetIngestData"});
export const PlaylistFragmentDoc = new TypedDocumentString(`
    fragment Playlist on StreamPlaylist {
  id
  name
  provider
  header
  labels {
    id
    name
    type
  }
  searchQuery
  locked
  metadata
  partnerName
  partnerPlaylistAlias
  items {
    ... on PositionedPlaylistAsset {
      position
      asset {
        id
        provider
      }
    }
    ... on PositionedPlaylistItemPlaylist {
      position
      playlist {
        id
        provider
      }
    }
  }
}
    `, {"fragmentName":"Playlist"});
export const AssetHistoryDocument = new TypedDocumentString(`
    query AssetHistory($newsroom: String!, $assetId: Int!) {
  assetHistory(newsroom: $newsroom, assetId: $assetId)
}
    `);
export const CategoryHistoryDocument = new TypedDocumentString(`
    query CategoryHistory($newsroom: String!, $categoryId: Int!) {
  categoryHistory(newsroom: $newsroom, categoryId: $categoryId)
}
    `);
export const SearchAuthorsDocument = new TypedDocumentString(`
    query SearchAuthors($provider: String!, $query: String!) {
  searchAuthors(provider: $provider, query: $query) {
    ...AuthorDataFields
  }
}
    fragment AuthorDataFields on Author {
  title
  slug
  id
  contact {
    type
    value
  }
}`);
export const AuthorsDocument = new TypedDocumentString(`
    query Authors($provider: String!, $query: [String!]!) {
  authors(provider: $provider, ids: $query) {
    ...AuthorDataFields
  }
}
    fragment AuthorDataFields on Author {
  title
  slug
  id
  contact {
    type
    value
  }
}`);
export const SearchTagsDocument = new TypedDocumentString(`
    query SearchTags($provider: String!, $query: String!, $page: Int, $limit: Int) {
  searchTags(provider: $provider, query: $query, page: $page, limit: $limit) {
    ...TagDataFields
  }
}
    fragment TagDataFields on Tag {
  id
  title
  type
}`);
export const SuggestTagsDocument = new TypedDocumentString(`
    query SuggestTags($provider: String!, $title: String!, $description: String, $tags: [String!], $subtitlesUrls: [String!], $limit: Int) {
  suggestTags(
    provider: $provider
    title: $title
    description: $description
    tags: $tags
    subtitlesUrls: $subtitlesUrls
    limit: $limit
  ) {
    ...TagDataFields
  }
}
    fragment TagDataFields on Tag {
  id
  title
  type
}`);
export const ListTagsDocument = new TypedDocumentString(`
    query ListTags($provider: String!, $ids: [String!]!) {
  listTags(provider: $provider, ids: $ids) {
    ...TagDataFields
  }
}
    fragment TagDataFields on Tag {
  id
  title
  type
}`);
export const StoriesDocument = new TypedDocumentString(`
    query Stories($provider: String!, $ids: [String!]!) {
  stories(provider: $provider, ids: $ids) {
    ...StoryDataFields
  }
}
    fragment StoryDataFields on Story {
  id
  title
}`);
export const SearchStoriesDocument = new TypedDocumentString(`
    query SearchStories($provider: String!, $query: String!, $page: Int, $limit: Int) {
  searchStories(provider: $provider, query: $query, page: $page, limit: $limit) {
    ...StoryDataFields
  }
}
    fragment StoryDataFields on Story {
  id
  title
}`);
export const CreateAssetPreviewDocument = new TypedDocumentString(`
    mutation CreateAssetPreview($assetId: Int!, $provider: String!, $editedBy: String!, $cutRange: CutRange!, $artifacts: [String!]!) {
  createAssetPreview(
    assetId: $assetId
    provider: $provider
    editedBy: $editedBy
    cutRange: $cutRange
    artifacts: $artifacts
  ) {
    ... on PreviewSuccessResponse {
      result
    }
    ... on PreviewErrorResponse {
      result
      message
    }
  }
}
    `);
export const UploadAssetPreviewDocument = new TypedDocumentString(`
    mutation UploadAssetPreview($assetId: Int!, $provider: String!, $editedBy: String!, $source: String!, $prefix: String, $artifacts: [String!]!) {
  uploadAssetPreview(
    assetId: $assetId
    provider: $provider
    editedBy: $editedBy
    source: $source
    prefix: $prefix
    artifacts: $artifacts
  ) {
    ... on PreviewSuccessResponse {
      result
    }
    ... on PreviewErrorResponse {
      result
      message
    }
  }
}
    `);
export const UploadCategoryPreviewDocument = new TypedDocumentString(`
    mutation UploadCategoryPreview($categoryId: Int!, $provider: String!, $editedBy: String!, $source: String!, $artifacts: [String!]!) {
  uploadCategoryPreview(
    categoryId: $categoryId
    provider: $provider
    editedBy: $editedBy
    source: $source
    artifacts: $artifacts
  ) {
    ... on PreviewSuccessResponse {
      result
    }
    ... on PreviewErrorResponse {
      message
      result
    }
  }
}
    `);
export const LastAssetIngestDocument = new TypedDocumentString(`
    query LastAssetIngest($assetCriteria: AssetCriteria!) {
  lastAssetIngest(assetCriteria: $assetCriteria) {
    ...LastAssetIngestData
  }
}
    fragment LastAssetIngestData on LastAssetIngest {
  __typename
  ... on AssetIngestNone {
    assetId
    provider
  }
  ... on AssetIngest {
    assetId
    provider
    type
  }
  ... on AssetIngestInProgress {
    totalProgress
  }
}`);
export const LastAssetsIngestDocument = new TypedDocumentString(`
    query LastAssetsIngest($assetsCriteria: [AssetCriteria!]!) {
  lastAssetsIngest(assetsCriteria: $assetsCriteria) {
    ...LastAssetIngestData
  }
}
    fragment LastAssetIngestData on LastAssetIngest {
  __typename
  ... on AssetIngestNone {
    assetId
    provider
  }
  ... on AssetIngest {
    assetId
    provider
    type
  }
  ... on AssetIngestInProgress {
    totalProgress
  }
}`);
export const LiveEncodersDocument = new TypedDocumentString(`
    query LiveEncoders($newsroom: String!) {
  encoders(newsroom: $newsroom) {
    id
    name
    url
    additionalFeatures
    inputs {
      id
      name
      type
      event {
        id
        schedule {
          startTime
          endTime
        }
        status
        asset {
          id
          provider
        }
      }
    }
    events(newsroom: $newsroom) {
      id
      createTime
      schedule {
        startTime
        endTime
      }
      status
      encoder {
        id
        name
      }
      input {
        id
        name
      }
      asset {
        id
        provider
        additional {
          metadata
        }
      }
      notifications {
        timestamp
        level
        message
        code
      }
      networkInputUri
      hasLivePreview
    }
  }
  channels: mediaLiveChannels(newsroom: $newsroom) {
    id
    state
    input {
      type
      urls
      srtSettings {
        streamId
        address
        port
        latency
        algorithm
        passphrase
      }
    }
    asset {
      id
      provider
    }
    schedule {
      startTime
      endTime
    }
  }
}
    `);
export const MediaLiveInputsDocument = new TypedDocumentString(`
    query MediaLiveInputs($newsroom: String!, $reserved: Boolean = true) {
  mediaLiveInputs(where: {newsroom: $newsroom, reserved: $reserved}) {
    id
    name
    type
    urls
    channel {
      asset {
        id
        provider
      }
    }
    srtSettings {
      streamId
      address
      port
      latency
      algorithm
      passphrase
    }
  }
}
    `);
export const CuttingroomChannelsDocument = new TypedDocumentString(`
    query CuttingroomChannels($newsroom: String!) {
  cuttingroomChannels(newsroom: $newsroom) {
    name
    server
    streamKey
  }
}
    `);
export const LiveArchiveDocument = new TypedDocumentString(`
    query LiveArchive($newsroom: String!, $assetId: Int!) {
  liveArchive(newsroom: $newsroom, assetId: $assetId) {
    logoUrl
  }
}
    `);
export const CreateLiveStreamDocument = new TypedDocumentString(`
    mutation CreateLiveStream($newsroom: String!, $metadata: CreateLiveStreamMetadataInput!, $schedule: LiveStreamScheduleInput, $livePreview: Boolean, $liveArchive: Boolean, $vertical: Boolean, $setup: LiveStreamSetupInput!, $overlay: LiveStreamOverlayInput, $htmlOverlay: LiveStreamHTMLOverlayInput, $deinterlace: Boolean, $audioNormalization: Boolean, $audioOnly: Boolean, $wscRtmpOutput: Boolean, $drmEncryption: Boolean) {
  createLiveStream(
    input: {newsroom: $newsroom, metadata: $metadata, schedule: $schedule, livePreview: $livePreview, liveArchive: $liveArchive, vertical: $vertical, setup: $setup, overlay: $overlay, htmlOverlay: $htmlOverlay, deinterlace: $deinterlace, audioNormalization: $audioNormalization, audioOnly: $audioOnly, wscRtmpOutput: $wscRtmpOutput, drmEncryption: $drmEncryption}
  ) {
    asset {
      id
      provider
    }
  }
}
    `);
export const PrepareLiveStreamDocument = new TypedDocumentString(`
    mutation PrepareLiveStream($newsroom: String!, $assetId: Int!, $metadata: PrepareLiveStreamMetadataInput, $schedule: LiveStreamScheduleInput, $livePreview: Boolean, $liveArchive: Boolean, $vertical: Boolean, $setup: LiveStreamSetupInput!, $overlay: LiveStreamOverlayInput, $htmlOverlay: LiveStreamHTMLOverlayInput, $deinterlace: Boolean, $audioNormalization: Boolean, $audioOnly: Boolean, $wscRtmpOutput: Boolean, $drmEncryption: Boolean) {
  prepareLiveStream(
    input: {newsroom: $newsroom, assetId: $assetId, metadata: $metadata, schedule: $schedule, livePreview: $livePreview, liveArchive: $liveArchive, vertical: $vertical, setup: $setup, overlay: $overlay, htmlOverlay: $htmlOverlay, deinterlace: $deinterlace, audioNormalization: $audioNormalization, audioOnly: $audioOnly, wscRtmpOutput: $wscRtmpOutput, drmEncryption: $drmEncryption}
  ) {
    asset {
      id
      provider
    }
  }
}
    `);
export const StartLiveStreamDocument = new TypedDocumentString(`
    mutation StartLiveStream($newsroom: String!, $assetId: Int!, $editedBy: String) {
  startLiveStream(
    input: {newsroom: $newsroom, assetId: $assetId, editedBy: $editedBy}
  )
}
    `);
export const StopLiveStreamDocument = new TypedDocumentString(`
    mutation StopLiveStream($newsroom: String!, $assetId: Int!, $editedBy: String) {
  stopLiveStream(
    input: {newsroom: $newsroom, assetId: $assetId, editedBy: $editedBy}
  )
}
    `);
export const UpdateLiveStreamDocument = new TypedDocumentString(`
    mutation UpdateLiveStream($newsroom: String!, $assetId: Int!, $schedule: LiveStreamScheduleInput, $metadata: UpdateLiveStreamMetadataInput!) {
  updateLiveStream(
    input: {newsroom: $newsroom, assetId: $assetId, schedule: $schedule, metadata: $metadata}
  )
}
    `);
export const InsertAdDocument = new TypedDocumentString(`
    mutation InsertAd($newsroom: String!, $assetId: Int!, $duration: Int!, $offset: Int) {
  insertAd(
    input: {newsroom: $newsroom, assetId: $assetId, duration: $duration, offset: $offset}
  )
}
    `);
export const CancelAdDocument = new TypedDocumentString(`
    mutation CancelAd($newsroom: String!, $assetId: Int!) {
  cancelAd(input: {newsroom: $newsroom, assetId: $assetId})
}
    `);
export const UnpublishLiveAssetDocument = new TypedDocumentString(`
    mutation UnpublishLiveAsset($newsroom: String!, $assetId: Int!, $editedBy: String) {
  unpublishLiveAsset(
    input: {newsroom: $newsroom, assetId: $assetId, editedBy: $editedBy}
  )
}
    `);
export const EncoderInputPreviewDocument = new TypedDocumentString(`
    query EncoderInputPreview($encoderId: String!, $inputId: Int!, $width: Int!, $height: Int!) {
  encoderInputPreview(
    input: {sdi: {encoderId: $encoderId, inputId: $inputId}, size: {width: $width, height: $height}}
  ) {
    base64
  }
}
    `);
export const LiveStreamDocument = new TypedDocumentString(`
    query LiveStream($newsroom: String!, $assetId: Int!) {
  live(input: {newsroom: $newsroom, assetId: $assetId}) {
    __typename
    ... on ElementalLive {
      events {
        __typename
        ... on ElementalLiveEvent {
          dvrLength
          hasLivePreview
          hasWscRtmpOutput
          audioChannelsMix {
            stereoMix {
              leftChannel {
                channelId
                gain
              }
              rightChannel {
                channelId
                gain
              }
            }
          }
        }
      }
    }
    ... on ExternalLive {
      externalUrl
    }
    ... on RtmpLive {
      streamKey
    }
  }
}
    `);
export const CreateRtmpPushDocument = new TypedDocumentString(`
    mutation CreateRtmpPush($metadata: CreateLiveStreamMetadataInput!, $newsroom: String!, $streamKey: String!, $streamType: RtmpStreamTypeInput!) {
  createRtmpPush(
    input: {metadata: $metadata, newsroom: $newsroom, streamKey: $streamKey, streamType: $streamType}
  ) {
    asset {
      id
      provider
    }
  }
}
    `);
export const PrepareRtmpPushDocument = new TypedDocumentString(`
    mutation PrepareRtmpPush($metadata: PrepareLiveStreamMetadataInput!, $newsroom: String!, $assetId: Int!, $streamKey: String!, $streamType: RtmpStreamTypeInput!) {
  prepareRtmpPush(
    input: {metadata: $metadata, newsroom: $newsroom, assetId: $assetId, streamKey: $streamKey, streamType: $streamType}
  ) {
    asset {
      id
      provider
    }
  }
}
    `);
export const DuplicateLiveStreamDocument = new TypedDocumentString(`
    mutation DuplicateLiveStream($newsroom: String!, $assetId: Int!, $duplicatedBy: String!) {
  duplicateLiveStream(
    input: {newsroom: $newsroom, assetId: $assetId, duplicatedBy: $duplicatedBy}
  ) {
    asset {
      id
      provider
    }
  }
}
    `);
export const ChangeAssetRestrictionsDocument = new TypedDocumentString(`
    mutation ChangeAssetRestrictions($provider: String!, $assetId: Int!, $geoRestriction: GeoRestriction, $accessLevel: AccessLevel) {
  changeAssetRestrictions(
    input: {provider: $provider, assetId: $assetId, geoRestriction: $geoRestriction, accessLevel: $accessLevel}
  )
}
    `);
export const UploadSubtitlesDocument = new TypedDocumentString(`
    mutation UploadSubtitles($provider: String!, $assetId: Int!, $languageCode: String!, $source: String!) {
  uploadSubtitles(
    input: {provider: $provider, assetId: $assetId, languageCode: $languageCode, source: $source}
  ) {
    url
  }
}
    `);
export const StreamDurationDocument = new TypedDocumentString(`
    query StreamDuration($provider: String!, $assetId: Int!) {
  streamDuration(input: {provider: $provider, assetId: $assetId})
}
    `);
export const SignAssetStreamUrlsDocument = new TypedDocumentString(`
    mutation SignAssetStreamUrls($provider: String!, $assetId: Int!, $expirationTime: Int!) {
  signAssetStreamUrls(
    input: {provider: $provider, assetId: $assetId, expirationTime: $expirationTime}
  ) {
    format
    url
  }
}
    `);
export const GenerateDrmTokenDocument = new TypedDocumentString(`
    mutation GenerateDrmToken($provider: String!, $assetId: Int!) {
  generateDrmToken(input: {provider: $provider, assetId: $assetId}) {
    token
    expiry
  }
}
    `);
export const PlaylistsDocument = new TypedDocumentString(`
    query Playlists($provider: String!, $id: String, $name: String, $labels: [String!]! = [], $limit: Int, $page: Int) {
  providerPlaylists(
    filter: {provider: $provider, name: $name, id: $id, labels: $labels}
    limit: $limit
    page: $page
  ) {
    ...Playlist
  }
}
    fragment Playlist on StreamPlaylist {
  id
  name
  provider
  header
  labels {
    id
    name
    type
  }
  searchQuery
  locked
  metadata
  partnerName
  partnerPlaylistAlias
  items {
    ... on PositionedPlaylistAsset {
      position
      asset {
        id
        provider
      }
    }
    ... on PositionedPlaylistItemPlaylist {
      position
      playlist {
        id
        provider
      }
    }
  }
}`);
export const CreatePlaylistDocument = new TypedDocumentString(`
    mutation CreatePlaylist($provider: String!, $name: String!, $header: String, $labels: [PlaylistLabelInput!], $searchQuery: String, $metadata: JSON, $items: [PlaylistItemInput!]!, $partnerName: String, $partnerPlaylistAlias: String) {
  createPlaylist(
    data: {provider: $provider, name: $name, header: $header, labels: $labels, searchQuery: $searchQuery, metadata: $metadata, items: $items, partnerName: $partnerName, partnerPlaylistAlias: $partnerPlaylistAlias}
  ) {
    success
    playlist {
      ...Playlist
    }
    errors
  }
}
    fragment Playlist on StreamPlaylist {
  id
  name
  provider
  header
  labels {
    id
    name
    type
  }
  searchQuery
  locked
  metadata
  partnerName
  partnerPlaylistAlias
  items {
    ... on PositionedPlaylistAsset {
      position
      asset {
        id
        provider
      }
    }
    ... on PositionedPlaylistItemPlaylist {
      position
      playlist {
        id
        provider
      }
    }
  }
}`);
export const UpdatePlaylistDocument = new TypedDocumentString(`
    mutation UpdatePlaylist($id: String!, $provider: String!, $name: String!, $header: String, $labels: [PlaylistLabelInput!], $searchQuery: String, $metadata: JSON, $items: [PlaylistItemInput!]!, $partnerName: String, $partnerPlaylistAlias: String) {
  updatePlaylist(
    data: {id: $id, provider: $provider, name: $name, header: $header, labels: $labels, searchQuery: $searchQuery, metadata: $metadata, items: $items, partnerName: $partnerName, partnerPlaylistAlias: $partnerPlaylistAlias}
  ) {
    success
    playlist {
      ...Playlist
    }
    errors
  }
}
    fragment Playlist on StreamPlaylist {
  id
  name
  provider
  header
  labels {
    id
    name
    type
  }
  searchQuery
  locked
  metadata
  partnerName
  partnerPlaylistAlias
  items {
    ... on PositionedPlaylistAsset {
      position
      asset {
        id
        provider
      }
    }
    ... on PositionedPlaylistItemPlaylist {
      position
      playlist {
        id
        provider
      }
    }
  }
}`);
export const DeletePlaylistDocument = new TypedDocumentString(`
    mutation DeletePlaylist($id: String!, $provider: String!) {
  deletePlaylist(data: {id: $id, provider: $provider}) {
    success
    playlist {
      id
      items {
        ... on PositionedPlaylistAsset {
          position
          asset {
            id
            provider
          }
        }
        ... on PositionedPlaylistItemPlaylist {
          position
          playlist {
            id
            provider
          }
        }
      }
    }
  }
}
    `);
export const PodmeCategoriesDocument = new TypedDocumentString(`
    query PodmeCategories {
  podmeCategories {
    id
    name
  }
}
    `);
export const AssetPodmeMetadataDocument = new TypedDocumentString(`
    query AssetPodmeMetadata($provider: String!, $assetId: Int!) {
  assetPodmeMetadata(where: {provider: $provider, assetId: $assetId}) {
    sendToPodme
    podmeId
  }
}
    `);
export const CategoryPodmeMetadataDocument = new TypedDocumentString(`
    query CategoryPodmeMetadata($provider: String!, $categoryId: Int!) {
  categoryPodmeMetadata(where: {provider: $provider, categoryId: $categoryId}) {
    sendToPodme
    podmeId
    podmeCategories
    sendRssToPodme
  }
}
    `);
export const ModifyAssetPodmeMetadataDocument = new TypedDocumentString(`
    mutation ModifyAssetPodmeMetadata($provider: String!, $assetId: Int!, $metadata: AssetPodmeMetadataInput!) {
  modifyAssetPodmeMetadata(
    where: {provider: $provider, assetId: $assetId}
    metadata: $metadata
  ) {
    sendToPodme
    podmeId
  }
}
    `);
export const ModifyCategoryPodmeMetadataDocument = new TypedDocumentString(`
    mutation ModifyCategoryPodmeMetadata($provider: String!, $categoryId: Int!, $metadata: CategoryPodmeMetadataInput!) {
  modifyCategoryPodmeMetadata(
    where: {provider: $provider, categoryId: $categoryId}
    metadata: $metadata
  ) {
    sendToPodme
    podmeId
    podmeCategories
    sendRssToPodme
  }
}
    `);
export const SendPushNotificationDocument = new TypedDocumentString(`
    mutation SendPushNotification($provider: String!, $assetId: Int!, $notificationBody: String) {
  sendPushNotification(
    data: {provider: $provider, assetId: $assetId, notificationBody: $notificationBody}
  ) {
    __typename
    ... on SendPushNotificationSuccessResponse {
      pushIds
      numRecipients
    }
    ... on SendPushNotificationErrorResponse {
      errorMessage
    }
  }
}
    `);
export const AssetSchedulesDocument = new TypedDocumentString(`
    query AssetSchedules($provider: String, $assetId: Int, $action: AssetScheduleActions!) {
  assetSchedules(where: {provider: $provider, assetId: $assetId, action: $action}) {
    provider
    assetId
    time
    action
  }
}
    `);
export const CreateAssetScheduleDocument = new TypedDocumentString(`
    mutation CreateAssetSchedule($provider: String!, $assetId: Int!, $time: Int!, $action: AssetScheduleActions!) {
  createAssetSchedule(
    data: {provider: $provider, assetId: $assetId, time: $time, action: $action}
  ) {
    provider
    assetId
    time
    action
  }
}
    `);
export const UpdateAssetScheduleDocument = new TypedDocumentString(`
    mutation UpdateAssetSchedule($provider: String!, $assetId: Int!, $time: Int!, $action: AssetScheduleActions!) {
  updateAssetSchedule(
    where: {provider: $provider, assetId: $assetId, action: $action}
    data: {time: $time}
  ) {
    provider
    assetId
    time
    action
  }
}
    `);
export const DeleteAssetScheduleDocument = new TypedDocumentString(`
    mutation DeleteAssetSchedule($provider: String!, $assetId: Int!, $action: AssetScheduleActions!) {
  deleteAssetSchedule(
    where: {provider: $provider, assetId: $assetId, action: $action}
  )
}
    `);
export const NotificationSchedulesDocument = new TypedDocumentString(`
    query NotificationSchedules($provider: String, $assetId: Int, $action: NotificationScheduleActions!) {
  notificationSchedules(
    where: {provider: $provider, assetId: $assetId, action: $action}
  ) {
    provider
    assetId
    action
    time
    notificationBody
  }
}
    `);
export const CreateNotificationScheduleDocument = new TypedDocumentString(`
    mutation CreateNotificationSchedule($provider: String!, $assetId: Int!, $action: NotificationScheduleActions!, $time: Int!, $notificationBody: String) {
  createNotificationSchedule(
    data: {provider: $provider, assetId: $assetId, time: $time, action: $action, notificationBody: $notificationBody}
  ) {
    provider
    assetId
    action
    time
    notificationBody
  }
}
    `);
export const UpdateNotificationScheduleDocument = new TypedDocumentString(`
    mutation UpdateNotificationSchedule($provider: String!, $assetId: Int!, $action: NotificationScheduleActions!, $time: Int!, $notificationBody: String) {
  updateNotificationSchedule(
    where: {provider: $provider, assetId: $assetId, action: $action}
    data: {time: $time, notificationBody: $notificationBody}
  ) {
    provider
    assetId
    action
    time
    notificationBody
  }
}
    `);
export const DeleteNotificationScheduleDocument = new TypedDocumentString(`
    mutation DeleteNotificationSchedule($provider: String!, $assetId: Int!, $action: NotificationScheduleActions!) {
  deleteNotificationSchedule(
    where: {provider: $provider, assetId: $assetId, action: $action}
  )
}
    `);
export const EngagementStatsApiKeyDocument = new TypedDocumentString(`
    query EngagementStatsApiKey($provider: String!) {
  engagementStatsApiKey(provider: $provider) {
    value
  }
}
    `);
export const GenerateSubtitlesForAssetDocument = new TypedDocumentString(`
    mutation GenerateSubtitlesForAsset($id: Int!, $provider: String!) {
  generateSubtitlesForAsset(id: $id, provider: $provider) {
    success
    error
  }
}
    `);
export const DurationFromUrlDocument = new TypedDocumentString(`
    query DurationFromUrl($url: String!) {
  playlists(url: $url) {
    duration
  }
}
    `);
export const ConvertVideoDocument = new TypedDocumentString(`
    mutation ConvertVideo($provider: String!, $assetId: Int!) {
  convertVideo(input: {provider: $provider, assetId: $assetId}) {
    url
  }
}
    `);
export const PrepareLiveArchiveDocument = new TypedDocumentString(`
    mutation PrepareLiveArchive($provider: String!, $assetId: Int!, $cutRange: LiveArchiveCutRangeInput) {
  prepareLiveArchive(
    input: {provider: $provider, assetId: $assetId, cutRange: $cutRange}
  ) {
    url
  }
}
    `);
export const CropLiveArchiveDocument = new TypedDocumentString(`
    mutation CropLiveArchive($provider: String!, $assetId: Int!, $cutRange: LiveArchiveCutRangeInput!, $createdBy: String) {
  cropLiveArchive(
    input: {provider: $provider, assetId: $assetId, cutRange: $cutRange, createdBy: $createdBy}
  ) {
    assetId
    originAssetId
  }
}
    `);
export const LiveArchiveExpirationDocument = new TypedDocumentString(`
    query LiveArchiveExpiration($provider: String!, $assetId: Int!) {
  liveArchiveExpiration(input: {provider: $provider, assetId: $assetId})
}
    `);
export const CreateUserDocument = new TypedDocumentString(`
    mutation CreateUser($firstname: String, $lastname: String, $email: String!, $roles: [RoleInput!]!, $created: DateTime!) {
  createUser(
    data: {firstname: $firstname, lastname: $lastname, email: $email, roles: $roles, created: $created}
  ) {
    ... on User {
      email
      firstname
      lastname
      roles {
        newsroom
        roleType
      }
    }
    ... on ManageUserFailure {
      message
    }
  }
}
    `);
export const UpdateUserDocument = new TypedDocumentString(`
    mutation UpdateUser($firstname: String, $lastname: String, $email: String!, $roles: [RoleInput!]!) {
  updateUser(
    data: {firstname: $firstname, lastname: $lastname, email: $email, roles: $roles}
  ) {
    ... on User {
      email
      firstname
      lastname
      roles {
        newsroom
        roleType
      }
    }
    ... on ManageUserFailure {
      message
    }
  }
}
    `);
export const UsersDocument = new TypedDocumentString(`
    query Users($query: String!) {
  users(data: {query: $query}) {
    email
    firstname
    lastname
    roles {
      newsroom
      roleType
    }
  }
}
    `);
export const CreateClipsDocument = new TypedDocumentString(`
    mutation CreateClips($assetId: Int!, $provider: String!, $cutRanges: [WeakCutRange!]!, $protection: Protections) {
  createClips(
    data: {assetId: $assetId, provider: $provider, cutRanges: $cutRanges, protection: $protection}
  ) {
    ... on CreateClipsSuccessResponse {
      assetId
      originAssetId
    }
    ... on ErrorResponse {
      message
    }
  }
}
    `);
export const TrimAssetDocument = new TypedDocumentString(`
    mutation TrimAsset($assetId: Int!, $provider: String!, $cutRanges: [WeakCutRange!]!) {
  trimAsset(data: {assetId: $assetId, provider: $provider, cutRanges: $cutRanges})
}
    `);
export const TranscodeVideoDocument = new TypedDocumentString(`
    mutation TranscodeVideo($assetId: Int!, $newsroom: String!, $sourceUri: String!, $options: TranscodeVideoOptions) {
  transcodeVideo(
    assetId: $assetId
    newsroom: $newsroom
    sourceUri: $sourceUri
    options: $options
  ) {
    assetId
    requestId
  }
}
    `);
export const EnableDrmDocument = new TypedDocumentString(`
    mutation EnableDrm($assetId: Int!, $provider: String!) {
  enableDrm(assetId: $assetId, provider: $provider) {
    status
  }
}
    `);

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    AssetHistory: build.query<AssetHistoryQuery, AssetHistoryQueryVariables>({
      query: (variables) => ({ document: AssetHistoryDocument as unknown as string, variables })
    }),
    CategoryHistory: build.query<CategoryHistoryQuery, CategoryHistoryQueryVariables>({
      query: (variables) => ({ document: CategoryHistoryDocument as unknown as string, variables })
    }),
    SearchAuthors: build.query<SearchAuthorsQuery, SearchAuthorsQueryVariables>({
      query: (variables) => ({ document: SearchAuthorsDocument as unknown as string, variables })
    }),
    Authors: build.query<AuthorsQuery, AuthorsQueryVariables>({
      query: (variables) => ({ document: AuthorsDocument as unknown as string, variables })
    }),
    SearchTags: build.query<SearchTagsQuery, SearchTagsQueryVariables>({
      query: (variables) => ({ document: SearchTagsDocument as unknown as string, variables })
    }),
    SuggestTags: build.query<SuggestTagsQuery, SuggestTagsQueryVariables>({
      query: (variables) => ({ document: SuggestTagsDocument as unknown as string, variables })
    }),
    ListTags: build.query<ListTagsQuery, ListTagsQueryVariables>({
      query: (variables) => ({ document: ListTagsDocument as unknown as string, variables })
    }),
    Stories: build.query<StoriesQuery, StoriesQueryVariables>({
      query: (variables) => ({ document: StoriesDocument as unknown as string, variables })
    }),
    SearchStories: build.query<SearchStoriesQuery, SearchStoriesQueryVariables>({
      query: (variables) => ({ document: SearchStoriesDocument as unknown as string, variables })
    }),
    CreateAssetPreview: build.mutation<CreateAssetPreviewMutation, CreateAssetPreviewMutationVariables>({
      query: (variables) => ({ document: CreateAssetPreviewDocument as unknown as string, variables })
    }),
    UploadAssetPreview: build.mutation<UploadAssetPreviewMutation, UploadAssetPreviewMutationVariables>({
      query: (variables) => ({ document: UploadAssetPreviewDocument as unknown as string, variables })
    }),
    UploadCategoryPreview: build.mutation<UploadCategoryPreviewMutation, UploadCategoryPreviewMutationVariables>({
      query: (variables) => ({ document: UploadCategoryPreviewDocument as unknown as string, variables })
    }),
    LastAssetIngest: build.query<LastAssetIngestQuery, LastAssetIngestQueryVariables>({
      query: (variables) => ({ document: LastAssetIngestDocument as unknown as string, variables })
    }),
    LastAssetsIngest: build.query<LastAssetsIngestQuery, LastAssetsIngestQueryVariables>({
      query: (variables) => ({ document: LastAssetsIngestDocument as unknown as string, variables })
    }),
    LiveEncoders: build.query<LiveEncodersQuery, LiveEncodersQueryVariables>({
      query: (variables) => ({ document: LiveEncodersDocument as unknown as string, variables })
    }),
    MediaLiveInputs: build.query<MediaLiveInputsQuery, MediaLiveInputsQueryVariables>({
      query: (variables) => ({ document: MediaLiveInputsDocument as unknown as string, variables })
    }),
    CuttingroomChannels: build.query<CuttingroomChannelsQuery, CuttingroomChannelsQueryVariables>({
      query: (variables) => ({ document: CuttingroomChannelsDocument as unknown as string, variables })
    }),
    LiveArchive: build.query<LiveArchiveQuery, LiveArchiveQueryVariables>({
      query: (variables) => ({ document: LiveArchiveDocument as unknown as string, variables })
    }),
    CreateLiveStream: build.mutation<CreateLiveStreamMutation, CreateLiveStreamMutationVariables>({
      query: (variables) => ({ document: CreateLiveStreamDocument as unknown as string, variables })
    }),
    PrepareLiveStream: build.mutation<PrepareLiveStreamMutation, PrepareLiveStreamMutationVariables>({
      query: (variables) => ({ document: PrepareLiveStreamDocument as unknown as string, variables })
    }),
    StartLiveStream: build.mutation<StartLiveStreamMutation, StartLiveStreamMutationVariables>({
      query: (variables) => ({ document: StartLiveStreamDocument as unknown as string, variables })
    }),
    StopLiveStream: build.mutation<StopLiveStreamMutation, StopLiveStreamMutationVariables>({
      query: (variables) => ({ document: StopLiveStreamDocument as unknown as string, variables })
    }),
    UpdateLiveStream: build.mutation<UpdateLiveStreamMutation, UpdateLiveStreamMutationVariables>({
      query: (variables) => ({ document: UpdateLiveStreamDocument as unknown as string, variables })
    }),
    InsertAd: build.mutation<InsertAdMutation, InsertAdMutationVariables>({
      query: (variables) => ({ document: InsertAdDocument as unknown as string, variables })
    }),
    CancelAd: build.mutation<CancelAdMutation, CancelAdMutationVariables>({
      query: (variables) => ({ document: CancelAdDocument as unknown as string, variables })
    }),
    UnpublishLiveAsset: build.mutation<UnpublishLiveAssetMutation, UnpublishLiveAssetMutationVariables>({
      query: (variables) => ({ document: UnpublishLiveAssetDocument as unknown as string, variables })
    }),
    EncoderInputPreview: build.query<EncoderInputPreviewQuery, EncoderInputPreviewQueryVariables>({
      query: (variables) => ({ document: EncoderInputPreviewDocument as unknown as string, variables })
    }),
    LiveStream: build.query<LiveStreamQuery, LiveStreamQueryVariables>({
      query: (variables) => ({ document: LiveStreamDocument as unknown as string, variables })
    }),
    CreateRtmpPush: build.mutation<CreateRtmpPushMutation, CreateRtmpPushMutationVariables>({
      query: (variables) => ({ document: CreateRtmpPushDocument as unknown as string, variables })
    }),
    PrepareRtmpPush: build.mutation<PrepareRtmpPushMutation, PrepareRtmpPushMutationVariables>({
      query: (variables) => ({ document: PrepareRtmpPushDocument as unknown as string, variables })
    }),
    DuplicateLiveStream: build.mutation<DuplicateLiveStreamMutation, DuplicateLiveStreamMutationVariables>({
      query: (variables) => ({ document: DuplicateLiveStreamDocument as unknown as string, variables })
    }),
    ChangeAssetRestrictions: build.mutation<ChangeAssetRestrictionsMutation, ChangeAssetRestrictionsMutationVariables>({
      query: (variables) => ({ document: ChangeAssetRestrictionsDocument as unknown as string, variables })
    }),
    UploadSubtitles: build.mutation<UploadSubtitlesMutation, UploadSubtitlesMutationVariables>({
      query: (variables) => ({ document: UploadSubtitlesDocument as unknown as string, variables })
    }),
    StreamDuration: build.query<StreamDurationQuery, StreamDurationQueryVariables>({
      query: (variables) => ({ document: StreamDurationDocument as unknown as string, variables })
    }),
    SignAssetStreamUrls: build.mutation<SignAssetStreamUrlsMutation, SignAssetStreamUrlsMutationVariables>({
      query: (variables) => ({ document: SignAssetStreamUrlsDocument as unknown as string, variables })
    }),
    GenerateDrmToken: build.mutation<GenerateDrmTokenMutation, GenerateDrmTokenMutationVariables>({
      query: (variables) => ({ document: GenerateDrmTokenDocument as unknown as string, variables })
    }),
    Playlists: build.query<PlaylistsQuery, PlaylistsQueryVariables>({
      query: (variables) => ({ document: PlaylistsDocument as unknown as string, variables })
    }),
    CreatePlaylist: build.mutation<CreatePlaylistMutation, CreatePlaylistMutationVariables>({
      query: (variables) => ({ document: CreatePlaylistDocument as unknown as string, variables })
    }),
    UpdatePlaylist: build.mutation<UpdatePlaylistMutation, UpdatePlaylistMutationVariables>({
      query: (variables) => ({ document: UpdatePlaylistDocument as unknown as string, variables })
    }),
    DeletePlaylist: build.mutation<DeletePlaylistMutation, DeletePlaylistMutationVariables>({
      query: (variables) => ({ document: DeletePlaylistDocument as unknown as string, variables })
    }),
    PodmeCategories: build.query<PodmeCategoriesQuery, PodmeCategoriesQueryVariables | void>({
      query: (variables) => ({ document: PodmeCategoriesDocument as unknown as string, variables })
    }),
    AssetPodmeMetadata: build.query<AssetPodmeMetadataQuery, AssetPodmeMetadataQueryVariables>({
      query: (variables) => ({ document: AssetPodmeMetadataDocument as unknown as string, variables })
    }),
    CategoryPodmeMetadata: build.query<CategoryPodmeMetadataQuery, CategoryPodmeMetadataQueryVariables>({
      query: (variables) => ({ document: CategoryPodmeMetadataDocument as unknown as string, variables })
    }),
    ModifyAssetPodmeMetadata: build.mutation<ModifyAssetPodmeMetadataMutation, ModifyAssetPodmeMetadataMutationVariables>({
      query: (variables) => ({ document: ModifyAssetPodmeMetadataDocument as unknown as string, variables })
    }),
    ModifyCategoryPodmeMetadata: build.mutation<ModifyCategoryPodmeMetadataMutation, ModifyCategoryPodmeMetadataMutationVariables>({
      query: (variables) => ({ document: ModifyCategoryPodmeMetadataDocument as unknown as string, variables })
    }),
    SendPushNotification: build.mutation<SendPushNotificationMutation, SendPushNotificationMutationVariables>({
      query: (variables) => ({ document: SendPushNotificationDocument as unknown as string, variables })
    }),
    AssetSchedules: build.query<AssetSchedulesQuery, AssetSchedulesQueryVariables>({
      query: (variables) => ({ document: AssetSchedulesDocument as unknown as string, variables })
    }),
    CreateAssetSchedule: build.mutation<CreateAssetScheduleMutation, CreateAssetScheduleMutationVariables>({
      query: (variables) => ({ document: CreateAssetScheduleDocument as unknown as string, variables })
    }),
    UpdateAssetSchedule: build.mutation<UpdateAssetScheduleMutation, UpdateAssetScheduleMutationVariables>({
      query: (variables) => ({ document: UpdateAssetScheduleDocument as unknown as string, variables })
    }),
    DeleteAssetSchedule: build.mutation<DeleteAssetScheduleMutation, DeleteAssetScheduleMutationVariables>({
      query: (variables) => ({ document: DeleteAssetScheduleDocument as unknown as string, variables })
    }),
    NotificationSchedules: build.query<NotificationSchedulesQuery, NotificationSchedulesQueryVariables>({
      query: (variables) => ({ document: NotificationSchedulesDocument as unknown as string, variables })
    }),
    CreateNotificationSchedule: build.mutation<CreateNotificationScheduleMutation, CreateNotificationScheduleMutationVariables>({
      query: (variables) => ({ document: CreateNotificationScheduleDocument as unknown as string, variables })
    }),
    UpdateNotificationSchedule: build.mutation<UpdateNotificationScheduleMutation, UpdateNotificationScheduleMutationVariables>({
      query: (variables) => ({ document: UpdateNotificationScheduleDocument as unknown as string, variables })
    }),
    DeleteNotificationSchedule: build.mutation<DeleteNotificationScheduleMutation, DeleteNotificationScheduleMutationVariables>({
      query: (variables) => ({ document: DeleteNotificationScheduleDocument as unknown as string, variables })
    }),
    EngagementStatsApiKey: build.query<EngagementStatsApiKeyQuery, EngagementStatsApiKeyQueryVariables>({
      query: (variables) => ({ document: EngagementStatsApiKeyDocument as unknown as string, variables })
    }),
    GenerateSubtitlesForAsset: build.mutation<GenerateSubtitlesForAssetMutation, GenerateSubtitlesForAssetMutationVariables>({
      query: (variables) => ({ document: GenerateSubtitlesForAssetDocument as unknown as string, variables })
    }),
    DurationFromUrl: build.query<DurationFromUrlQuery, DurationFromUrlQueryVariables>({
      query: (variables) => ({ document: DurationFromUrlDocument as unknown as string, variables })
    }),
    ConvertVideo: build.mutation<ConvertVideoMutation, ConvertVideoMutationVariables>({
      query: (variables) => ({ document: ConvertVideoDocument as unknown as string, variables })
    }),
    PrepareLiveArchive: build.mutation<PrepareLiveArchiveMutation, PrepareLiveArchiveMutationVariables>({
      query: (variables) => ({ document: PrepareLiveArchiveDocument as unknown as string, variables })
    }),
    CropLiveArchive: build.mutation<CropLiveArchiveMutation, CropLiveArchiveMutationVariables>({
      query: (variables) => ({ document: CropLiveArchiveDocument as unknown as string, variables })
    }),
    LiveArchiveExpiration: build.query<LiveArchiveExpirationQuery, LiveArchiveExpirationQueryVariables>({
      query: (variables) => ({ document: LiveArchiveExpirationDocument as unknown as string, variables })
    }),
    CreateUser: build.mutation<CreateUserMutation, CreateUserMutationVariables>({
      query: (variables) => ({ document: CreateUserDocument as unknown as string, variables })
    }),
    UpdateUser: build.mutation<UpdateUserMutation, UpdateUserMutationVariables>({
      query: (variables) => ({ document: UpdateUserDocument as unknown as string, variables })
    }),
    Users: build.query<UsersQuery, UsersQueryVariables>({
      query: (variables) => ({ document: UsersDocument as unknown as string, variables })
    }),
    CreateClips: build.mutation<CreateClipsMutation, CreateClipsMutationVariables>({
      query: (variables) => ({ document: CreateClipsDocument as unknown as string, variables })
    }),
    TrimAsset: build.mutation<TrimAssetMutation, TrimAssetMutationVariables>({
      query: (variables) => ({ document: TrimAssetDocument as unknown as string, variables })
    }),
    TranscodeVideo: build.mutation<TranscodeVideoMutation, TranscodeVideoMutationVariables>({
      query: (variables) => ({ document: TranscodeVideoDocument as unknown as string, variables })
    }),
    EnableDrm: build.mutation<EnableDrmMutation, EnableDrmMutationVariables>({
      query: (variables) => ({ document: EnableDrmDocument as unknown as string, variables })
    }),
  }),
});

export { injectedRtkApi as api };
export const { useAssetHistoryQuery, useLazyAssetHistoryQuery, useCategoryHistoryQuery, useLazyCategoryHistoryQuery, useSearchAuthorsQuery, useLazySearchAuthorsQuery, useAuthorsQuery, useLazyAuthorsQuery, useSearchTagsQuery, useLazySearchTagsQuery, useSuggestTagsQuery, useLazySuggestTagsQuery, useListTagsQuery, useLazyListTagsQuery, useStoriesQuery, useLazyStoriesQuery, useSearchStoriesQuery, useLazySearchStoriesQuery, useCreateAssetPreviewMutation, useUploadAssetPreviewMutation, useUploadCategoryPreviewMutation, useLastAssetIngestQuery, useLazyLastAssetIngestQuery, useLastAssetsIngestQuery, useLazyLastAssetsIngestQuery, useLiveEncodersQuery, useLazyLiveEncodersQuery, useMediaLiveInputsQuery, useLazyMediaLiveInputsQuery, useCuttingroomChannelsQuery, useLazyCuttingroomChannelsQuery, useLiveArchiveQuery, useLazyLiveArchiveQuery, useCreateLiveStreamMutation, usePrepareLiveStreamMutation, useStartLiveStreamMutation, useStopLiveStreamMutation, useUpdateLiveStreamMutation, useInsertAdMutation, useCancelAdMutation, useUnpublishLiveAssetMutation, useEncoderInputPreviewQuery, useLazyEncoderInputPreviewQuery, useLiveStreamQuery, useLazyLiveStreamQuery, useCreateRtmpPushMutation, usePrepareRtmpPushMutation, useDuplicateLiveStreamMutation, useChangeAssetRestrictionsMutation, useUploadSubtitlesMutation, useStreamDurationQuery, useLazyStreamDurationQuery, useSignAssetStreamUrlsMutation, useGenerateDrmTokenMutation, usePlaylistsQuery, useLazyPlaylistsQuery, useCreatePlaylistMutation, useUpdatePlaylistMutation, useDeletePlaylistMutation, usePodmeCategoriesQuery, useLazyPodmeCategoriesQuery, useAssetPodmeMetadataQuery, useLazyAssetPodmeMetadataQuery, useCategoryPodmeMetadataQuery, useLazyCategoryPodmeMetadataQuery, useModifyAssetPodmeMetadataMutation, useModifyCategoryPodmeMetadataMutation, useSendPushNotificationMutation, useAssetSchedulesQuery, useLazyAssetSchedulesQuery, useCreateAssetScheduleMutation, useUpdateAssetScheduleMutation, useDeleteAssetScheduleMutation, useNotificationSchedulesQuery, useLazyNotificationSchedulesQuery, useCreateNotificationScheduleMutation, useUpdateNotificationScheduleMutation, useDeleteNotificationScheduleMutation, useEngagementStatsApiKeyQuery, useLazyEngagementStatsApiKeyQuery, useGenerateSubtitlesForAssetMutation, useDurationFromUrlQuery, useLazyDurationFromUrlQuery, useConvertVideoMutation, usePrepareLiveArchiveMutation, useCropLiveArchiveMutation, useLiveArchiveExpirationQuery, useLazyLiveArchiveExpirationQuery, useCreateUserMutation, useUpdateUserMutation, useUsersQuery, useLazyUsersQuery, useCreateClipsMutation, useTrimAssetMutation, useTranscodeVideoMutation, useEnableDrmMutation } = injectedRtkApi;

